import {
  buildAddressLabel,
  findRowByQueryWithType,
  parseCsv,
  parseDatasets,
  parseNumber
} from './partials/_data';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';
import {
  userCentricsConsentGiven,
  userCentricsOnUpdate
} from '../basisCookiebot/userCentrics';
import {
  addGeoJsonToMap,
  applyNoOverlap,
  csvRowsToGeoJson,
  pointInsidePolygons
} from './partials/_geo';
import { ensureMapLibraries } from './partials/_libraries';
import type { CsvRowWithDataset, Dataset } from './partials/_types';

/** Selector for any instance of this _Friendation_ component. */
const selector = '[is="block-network-expansion-map"]';

/**
 * Query OSM/Nominatim for a human-readable address.
 */
const geocodeWithOsm = async (query: string) => {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'json');
  url.searchParams.set('limit', '1');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('countrycodes', 'de');

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'de'
    }
  });

  if (!response.ok) {
    throw new Error(`OSM-Suche fehlgeschlagen (${response.status})`);
  }

  const results = await response.json();
  return results && results.length ? results[0] : null;
};

/**
 * Initialize one map component instance.
 */
const initComponent = async (component: HTMLElement) => {
  if (component.dataset.consentBound === 'true') return;
  component.dataset.consentBound = 'true';

  const mapEl = component.querySelector('#map') as HTMLElement | null;
  const debugEl = component.querySelector('#debug') as HTMLElement | null;
  const consentElement = component.querySelector(
    '[data-role="map-consent-notice"]'
  ) as HTMLElement | null;
  const mapContentElement = component.querySelector(
    '[data-role="map-consent-content"]'
  ) as HTMLElement | null;
  const inputCity = component.querySelector(
    '#address-city'
  ) as HTMLInputElement | null;
  const inputStreet = component.querySelector(
    '#address-street'
  ) as HTMLInputElement | null;
  const inputNumber = component.querySelector(
    '#address-number'
  ) as HTMLInputElement | null;
  const buttonSearch = component.querySelector(
    '#btn-search'
  ) as HTMLButtonElement | null;
  const buttonFit = component.querySelector(
    '#btn-fit'
  ) as HTMLButtonElement | null;
  const buttonToggle = component.querySelector(
    '#btn-toggle'
  ) as HTMLButtonElement | null;

  if (!mapEl) return;

  let mapInitialized = false;

  /** Write debug information to the sidebar or console fallback. */
  const logDebug = (value: unknown) => {
    if (debugEl) {
      debugEl.textContent =
        typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      return;
    }
  };

  const initMap = async () => {
    const rawDatasetsAttr = component.getAttribute('data-datasets');
    const datasetsCountAttr = component.getAttribute('data-datasets-count');
    const datasets = parseDatasets(rawDatasetsAttr);

    if (!datasets.length) {
      logDebug({
        error:
          'Keine Datensaetze gefunden. Bitte Matrixfeld "blockNetworkExpansionMap" mit ID/Status/Datei befuellen.',
        datasetsCountAttr,
        rawDatasetsAttr
      });
      return;
    }

    let libraries = null;
    try {
      libraries = await ensureMapLibraries();
    } catch (error) {
      logDebug({ error: String(error) });
      return;
    }

    if (!libraries) {
      logDebug({
        error: 'Leaflet/proj4/turf konnten nicht initialisiert werden.'
      });
      return;
    }

    const { L, proj4, turf } = libraries;
    const markerIcon = L.icon({
      iconRetinaUrl: markerIcon2xUrl,
      iconUrl: markerIconUrl,
      shadowUrl: markerShadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });

    /** EPSG:25832 (ETRS89 / UTM zone 32N) -> WGS84. */
    proj4.defs(
      'EPSG:25832',
      '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs'
    );

    const computedHeight = window.getComputedStyle(mapEl).height;

    const map = L.map(mapEl, { scrollWheelZoom: true }).setView(
      [51.168, 6.935],
      13
    );

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    let polygonsLayer: any = null;
    let polygonsGeoJson: any = null;
    let csvRowsCache: CsvRowWithDataset[] = [];
    let searchMarker: any = null;

    const applyManualPolygons = (geoJsonData: any) => {
      if (!geoJsonData?.features?.length) return geoJsonData;

      return {
        ...geoJsonData,
        features: geoJsonData.features.map((feature: any) => {
          const dataset = datasets.find(
            (item) => item.id === feature?.properties?.datasetId
          );

          if (!dataset?.polygon) return feature;

          return {
            ...feature,
            geometry: dataset.polygon,
            properties: {
              ...feature.properties,
              hullType: 'manual'
            }
          };
        })
      };
    };

    /** Create polygons from rows and re-render the map layer. */
    const refreshPolygons = (rows: CsvRowWithDataset[]) => {
      const geoJsonData = csvRowsToGeoJson(rows, datasets, proj4, turf);
      const adjustedGeoJsonData = applyManualPolygons(geoJsonData);

      if (adjustedGeoJsonData?.features?.length) {
        adjustedGeoJsonData.features = applyNoOverlap(
          adjustedGeoJsonData.features,
          turf
        );
      }

      if (polygonsLayer) {
        map.removeLayer(polygonsLayer);
      }

      polygonsLayer = addGeoJsonToMap(map, L, adjustedGeoJsonData, logDebug);
      polygonsGeoJson = adjustedGeoJsonData;

      return adjustedGeoJsonData;
    };

    /** Fit map viewport to polygon bounds. */
    const fitBounds = () => {
      if (!polygonsLayer) return;

      const bounds = polygonsLayer.getBounds();
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    };

    /** Toggle polygon layer visibility. */
    const togglePolygons = () => {
      if (!polygonsLayer) return;

      if (map.hasLayer(polygonsLayer)) {
        map.removeLayer(polygonsLayer);
        return;
      }

      polygonsLayer.addTo(map);
      fitBounds();
    };

    /** Place marker for an address row and center the map. */
    const showSearchResult = (row: CsvRowWithDataset) => {
      const x = parseNumber(row['koordinate x']);
      const y = parseNumber(row['koordinate y']);

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        logDebug({ error: 'Ungültige Koordinaten für diese Adresse.' });
        return;
      }

      const [lng, lat] = proj4('EPSG:25832', 'WGS84', [x, y]);
      const label = buildAddressLabel(row);

      if (searchMarker) map.removeLayer(searchMarker);

      searchMarker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
      searchMarker.bindPopup(`<strong>${label}</strong>`).openPopup();
      map.setView([lat, lng], 18, { animate: true });
    };

    /**
     * Search workflow:
     * 1) Try exact/partial CSV match.
     * 2) Fallback to OSM geocoding.
     */
    const runSearch = async () => {
      if (!inputCity || !inputStreet || !inputNumber) return;

      const city = inputCity.value.trim();
      const street = inputStreet.value.trim();
      const number = inputNumber.value.trim();

      const query = [street, number].filter(Boolean).join(' ').trim();
      const osmQuery = [street, number, city].filter(Boolean).join(' ').trim();

      if (!query && !osmQuery) {
        logDebug({ error: 'Bitte Stadt/Straße/Hausnummer eingeben.' });
        return;
      }

      const row = csvRowsCache.length
        ? findRowByQueryWithType(query, csvRowsCache)
        : null;
      if (row) {
        showSearchResult(row);

        const rowDataset = row._dataset as Dataset;
        const status = rowDataset?.status || 'Unbekannt';
        const gebietsname = row['gebietsname'] || rowDataset?.name || '';

        const osmLookupQuery = [
          row['straßenname'],
          row['hausnummer'],
          row['stadt']
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        let found = buildAddressLabel(row);

        if (osmLookupQuery) {
          try {
            const geocodeResult = await geocodeWithOsm(osmLookupQuery);
            if (geocodeResult?.display_name) {
              found = geocodeResult.display_name;
            }
          } catch {
            // Keep CSV fallback label if OSM lookup fails.
          }
        }

        logDebug({
          message: `Gebietsstatus: ${status}.`,
          gebietsname,
          found
        });

        return;
      }

      try {
        const result = await geocodeWithOsm(osmQuery || query);
        if (!result) {
          logDebug({
            error: 'Keine OSM-Treffer gefunden.',
            query: osmQuery || query
          });
          return;
        }

        const lng = Number.parseFloat(result.lon);
        const lat = Number.parseFloat(result.lat);

        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
          logDebug({
            error: 'OSM-Treffer ohne Koordinaten.',
            query: osmQuery || query
          });
          return;
        }

        if (searchMarker) map.removeLayer(searchMarker);

        searchMarker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
        searchMarker
          .bindPopup(`<strong>${result.display_name}</strong>`)
          .openPopup();
        map.setView([lat, lng], 18, { animate: true });

        const inside = pointInsidePolygons(lng, lat, polygonsGeoJson, turf);
        if (!inside) {
          logDebug({
            message: 'Ihre Adresse ist leider nicht dabei.',
            found: result.display_name
          });
          return;
        }

        logDebug({
          found: result.display_name,
          note: 'Adresse liegt innerhalb der Polygonfläche.'
        });
      } catch (error) {
        logDebug({ error: String(error) });
      }
    };

    /** Load all configured CSV datasets and initialize map polygons. */
    const initFromCsv = async () => {
      try {
        const loadedSets = await Promise.all(
          datasets.map(async (dataset) => {
            const response = await fetch(dataset.file, { cache: 'no-store' });
            if (!response.ok) {
              throw new Error(
                `CSV konnte nicht geladen werden (${response.status}): ${dataset.file}`
              );
            }

            const text = await response.text();
            const rows = parseCsv(text).map((row) => ({
              ...row,
              _dataset: dataset
            })) as CsvRowWithDataset[];

            return { dataset, rows };
          })
        );

        const rows = loadedSets.flatMap((item) => item.rows);
        csvRowsCache = rows;

        const initialGeoJson = refreshPolygons(rows);
        const rowsByDataset = datasets.map((dataset) => ({
          id: dataset.id,
          name: dataset.name,
          rows: rows.filter((row) => row._dataset?.id === dataset.id).length
        }));

        logDebug({
          mapContainerComputedHeight: computedHeight,
          csvRows: rows.length,
          datasets: datasets.length,
          manualPolygons: datasets.filter((dataset) => dataset.polygon).length,
          features: initialGeoJson?.features?.length,
          datasetRows: rowsByDataset
        });
      } catch (error) {
        logDebug({ error: String(error) });
      }
    };

    if (buttonFit) {
      buttonFit.addEventListener('click', fitBounds);
    }

    if (buttonToggle) {
      buttonToggle.addEventListener('click', togglePolygons);
    }

    if (buttonSearch) {
      buttonSearch.addEventListener('click', () => {
        void runSearch();
      });
    }

    [inputCity, inputStreet, inputNumber].forEach((input) => {
      if (!input) return;

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          void runSearch();
        }
      });
    });

    await initFromCsv();

    setTimeout(() => {
      map.invalidateSize();
    }, 0);
  };

  const updateConsent = () => {
    const marketingConsentGiven = userCentricsConsentGiven('marketing');

    if (consentElement) {
      consentElement.inert = marketingConsentGiven;
    }

    if (mapContentElement) {
      mapContentElement.inert = !marketingConsentGiven;
    }

    if (marketingConsentGiven && !mapInitialized) {
      mapInitialized = true;
      void initMap();
    }
  };

  updateConsent();
  userCentricsOnUpdate(updateConsent);
};

/**
 * Friendation component entrypoint.
 */
export const blockNetworkExpansionMap = () => {
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    void initComponent(component);
  });
};
